import { useNuxtApp } from 'nuxt/app';
import type {
  ContactErrorData,
  ContactRequest,
  ContactResponse,
} from '~/types/components/landing/Contact';
import type { ApiError, ApiInstance } from '~/types/plugins/api';
import ContactPage from '~/utils/contents/pages/ContactPage';

const CONNECTION_ERROR =
  'Unable to connect. Please check your internet connection.';
const SERVER_ERROR = 'Server error. Please try again later.';
const GENERIC_ERROR = 'Something went wrong. Please try again.';

type ContactFieldErrors = {
  first_name?: string;
  last_name?: string;
  email_address?: string;
  message?: string;
};

/**
 * Contact form state and submission logic for the contact page.
 *
 * POSTs `{ first_name, last_name, email_address, message }` to
 * `<API_BASE_URL>/contact-us` via the API client, mapping failures to
 * user-friendly messages by status code.
 */
export function useContact() {
  const { $apiClient } = useNuxtApp() as unknown as { $apiClient: ApiInstance };

  const form = reactive({
    firstName: '',
    lastName: '',
    email: '',
    message: '',
  });
  const pending = ref(false);
  const isSuccess = ref(false);
  const isError = ref(false);
  const errorMessage = ref('');
  const fieldErrors = ref<ContactFieldErrors>({});

  const applyError = (err: ApiError): void => {
    const status = err?.status;
    const data = err?.data as ContactErrorData | undefined;

    // status 0 / undefined is set by the API client for network & timeout errors
    if (!status) {
      errorMessage.value = CONNECTION_ERROR;
      return;
    }

    if (status === 422) {
      const errors = data?.errors;
      const mappedFieldErrors: ContactFieldErrors = {
        first_name: errors?.first_name?.[0],
        last_name: errors?.last_name?.[0],
        email_address: errors?.email_address?.[0],
        message: errors?.message?.[0],
      };
      const hasFieldErrors = Object.values(mappedFieldErrors).some(Boolean);

      if (hasFieldErrors) {
        fieldErrors.value = mappedFieldErrors;
        return;
      }

      errorMessage.value = data?.message || err?.message || GENERIC_ERROR;
      return;
    }

    if (status >= 500) {
      errorMessage.value = SERVER_ERROR;
      return;
    }

    errorMessage.value = data?.message || err?.message || GENERIC_ERROR;
  };

  const submit = async (): Promise<boolean> => {
    if (pending.value) return false;

    pending.value = true;
    isError.value = false;
    errorMessage.value = '';
    fieldErrors.value = {};

    try {
      const payload: ContactRequest = {
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        email_address: form.email.trim(),
        message: form.message.trim(),
      };
      await $apiClient.post<ContactResponse>(ContactPage.contact.path, payload);

      isSuccess.value = true;
      return true;
    } catch (err) {
      isError.value = true;
      applyError(err as ApiError);
      return false;
    } finally {
      pending.value = false;
    }
  };

  /** Clears the error state once the user starts editing again. */
  const clearError = (): void => {
    if (isError.value) {
      isError.value = false;
      errorMessage.value = '';
      fieldErrors.value = {};
    }
  };

  /** Resets the form back to its initial state. */
  const reset = (): void => {
    form.firstName = '';
    form.lastName = '';
    form.email = '';
    form.message = '';
    pending.value = false;
    isSuccess.value = false;
    isError.value = false;
    errorMessage.value = '';
    fieldErrors.value = {};
  };

  return {
    form,
    pending,
    isSuccess,
    isError,
    errorMessage,
    fieldErrors,
    submit,
    clearError,
    reset,
  };
}
